// Dashboard — the /admin/entitlements landing page.
// Pulls live tenant entitlement data via EntitlementsStore.load(), aggregates
// it into the 4 hero cards that the mockup specifies, and links to the SKU view.

import {useEffect} from "react";
import {inject, observer} from "mobx-react";
import {Link} from "react-router-dom";

const Dashboard = ({userStore, entitlementsStore}) => {
  useEffect(() => {
    entitlementsStore.load();
  }, [entitlementsStore]);

  const greetingName = () => {
    const u = userStore.userName || "";
    const local = u.split("@")[0];
    if (!local) return "Welcome";
    const first = local.split(/[._-]/)[0];
    return first.charAt(0).toUpperCase() + first.slice(1);
  };

  const totalAssigned = entitlementsStore.grants
    .filter(g => g.user_id !== "*")
    .length;

  const tenantWideSkus = entitlementsStore.skuSummary.filter(s => s.tenantWide).length;
  const skuCount = entitlementsStore.skuSummary.length;

  // Next-expiring grant across all SKUs.
  const nextExpiry = entitlementsStore.grants
    .map(g => g.expires_at)
    .filter(Boolean)
    .sort()[0];

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, {month: "short", day: "numeric", year: "numeric"}); }
    catch { return iso; }
  };

  return (
    <>
      <div className="page-hero">
        <div>
          <div className="adminEnt-eyebrow section-eyebrow">{userStore.tenantID || "Tenant"}</div>
          <h1 className="serif hero-display">Good morning, <em>{greetingName()}</em>.</h1>
          <p className="lede">
            Manage your organization's Plex entitlements, seat assignments, and
            renewal calendar. All changes take effect within one minute.
          </p>
        </div>
        <div className="page-hero-actions">
          <Link to="/admin/entitlements/skus" className="btn primary button-primary">
            Manage entitlements →
          </Link>
        </div>
      </div>

      {entitlementsStore.error && (
        <div className="err">
          Couldn't load entitlements: {entitlementsStore.error}
        </div>
      )}

      <div className="cards-row">
        <div className="card brand stone-card">
          <div className="card-eyebrow">Active SKUs</div>
          <div className="metric">{skuCount}</div>
          <div className="metric-sub">
            {tenantWideSkus} tenant-wide • {skuCount - tenantWideSkus} per-user
          </div>
        </div>
        <div className="card stone-card">
          <div className="card-eyebrow">Seats assigned</div>
          <div className="metric">{totalAssigned}</div>
          <div className="metric-sub">across per-user grants</div>
        </div>
        <div className="card stone-card">
          <div className="card-eyebrow">Next renewal</div>
          <div className="metric" style={{fontSize: 22, paddingTop: 6}}>
            {fmtDate(nextExpiry)}
          </div>
          <div className="metric-sub">earliest expiring grant</div>
        </div>
        <div className="card stone-card">
          <div className="card-eyebrow">Status</div>
          <div className="metric" style={{fontSize: 22, paddingTop: 6}}>
            {entitlementsStore.loading ? "Syncing…" : "Up to date"}
          </div>
          <div className="metric-sub">
            {entitlementsStore.lastFetchedAt
              ? `as of ${entitlementsStore.lastFetchedAt.toLocaleTimeString()}`
              : "—"}
          </div>
        </div>
      </div>

      <div className="section-label">Recent activity</div>
      <div className="empty">
        Audit timeline arrives with slice 3. For now, use the
        {" "}<Link to="/admin/entitlements/skus" style={{color: "var(--navy)"}}>SKU view</Link>{" "}
        to see who has what.
      </div>
    </>
  );
};

export default inject("userStore", "entitlementsStore")(observer(Dashboard));
